import { OSId } from '@ps/platform-message-framework/common/OSId';
import { Brand, Site } from '@ps/web-core-enums';
import { Cookie as CookieNames, FeatureFlag } from '@ps/web-core-store';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';

import StarsButton from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import CloseIcon from '@swf/core/assets/icons/close.svg';
import ShareApple from '@swf/core/assets/icons/share-apple.svg';
import useFeature from '@swf/core/hooks/useFeature';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { isIPadOS } from '@swf/core/utils';

import BannerNotification from '../../../components/BannerNotification/BannerNotification';

import {
  TXTWCL_Add_this_Web_App_to_your_homes_ELL,
  TXTWCL_Tap_N0_then_XXAdd_to_Homescree_ELL
} from './A2HS.messages.json';
import styles from './A2HS.scss';

const getIconFolder = (
  site: Site,
  brand: Brand,
  a2hsFeatureFlag: boolean,
  showVegasContent: boolean
): string => {
  const siteCode = Site[site];
  if (!a2hsFeatureFlag) {
    return Site[Site.COM];
  }

  if (showVegasContent) {
    if (site === Site.DESH && brand === Brand.PokerStarsCasino) {
      return 'DESH/Vegas';
    } else if (site === Site.DESH && brand !== Brand.PokerStarsCasino) {
      return 'DESH';
    }
    return 'vegas';
  } else if (brand === Brand.PokerStarsCasino && siteCode !== 'DESH') {
    return siteCode === 'EU' ? 'poker-stars/casino/EU' : 'poker-stars/casino';
  } else if (brand === Brand.PokerStars) {
    return siteCode === 'EU' ? 'poker-stars/poker/EU' : 'poker-stars/poker';
  } else if (brand === Brand.BetStars) {
    return siteCode === 'EU' ? 'poker-stars/sports/EU' : 'poker-stars/sports';
  } else if (siteCode === 'DESH' || siteCode === 'EU') {
    return siteCode;
  }

  return Site[Site.COM];
};

const A2HS: React.FC = () => {
  const a2hsFeatureFlag = useFeature(FeatureFlag.Add2HomeScreen);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const site = useSelector(select.cms.getSiteId);
  const brand = useSelector(select.cms.getBrandId);
  const showVegasContent = useSelector(select.cms.showVegasContent);
  const a2hs = useSelector(select.notification.getCookies)[CookieNames.ADD_TO_HOME_SCREEN];
  const cookieNotification = useSelector(select.notification.getCookies)[
    CookieNames.COOKIE_NOTIFICATION
  ];
  const os = useSelector(select.device.getOS) === OSId.I_OS || isIPadOS;
  const seo = useSelector(select.cms.getSEO);

  const {
    notification: { set }
  } = useDispatch<Dispatch>();

  const iconFolder = getIconFolder(site, brand, a2hsFeatureFlag, showVegasContent);
  const showBanner = !cookieNotification && a2hs && isAuthenticated && a2hsFeatureFlag && os;

  return (
    <>
      <Helmet>
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
        {seo && <meta name="apple-mobile-web-app-title" content={seo.title} />}
        <link rel="shortcut icon" href={`/static/icons/${iconFolder}/120.png`} />
        <link rel="apple-touch-icon" href={`/static/icons/${iconFolder}/120.png`} />
        <link rel="apple-touch-icon" sizes="152x152" href={`/static/icons/${iconFolder}/152.png`} />
        <link rel="apple-touch-icon" sizes="167x167" href={`/static/icons/${iconFolder}/167.png`} />
        <link rel="apple-touch-icon" sizes="180x180" href={`/static/icons/${iconFolder}/180.png`} />
      </Helmet>
      {showBanner && (
        <BannerNotification position="bottom" className={styles.banner}>
          <StarsButton
            role="button"
            className={styles.close}
            icon={<CloseIcon />}
            onClick={() => set({ name: CookieNames.ADD_TO_HOME_SCREEN, value: false })}
            size="small"
            variant="text"
          />
          <img src={`/static/icons/${iconFolder}/512.png`} className={styles.image} alt="" />
          <div className={styles.disclaimer}>
            <Translation tagName="p" {...TXTWCL_Add_this_Web_App_to_your_homes_ELL} />
            <Translation
              tagName="p"
              {...TXTWCL_Tap_N0_then_XXAdd_to_Homescree_ELL}
              values={{ 0: <ShareApple className={styles.shareApple} /> }}
            />
          </div>
        </BannerNotification>
      )}
    </>
  );
};

export default React.memo(A2HS);
